import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Snackbar,
} from '@mui/material';

import { useEffect, useState } from 'react';

import {
  getApiErrorMessage,
  getMixDelivery,
  updateMixDeliveryStatus,
} from '../../../../api/mix-deliveriesApi';

import {
  MIX_DELIVERY_STATUS,
  MIX_DELIVERY_STATUS_LABELS,
  MIX_STOCK_STATUS,
  MIX_STOCK_STATUS_LABELS,
} from '../types/mix-deliveries.types';

import MixStatusChangeDialog from './MixStatusChangeDialog';

export default function MixDeliveryDetailsDialog({
  deliveryId,
  open,
  onClose,
  onStatusChanged,
  userRole,
}) {
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targetStatus, setTargetStatus] = useState(null);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
  });

  useEffect(() => {
    if (!open || !deliveryId) {
      return;
    }

    let active = true;

    async function loadDelivery() {
      setLoading(true);
      setError(null);

      try {
        const result = await getMixDelivery(deliveryId);

        if (active) {
          setDelivery(result);
        }
      } catch (requestError) {
        if (active) {
          setError(getApiErrorMessage(requestError));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDelivery();

    return () => {
      active = false;
    };
  }, [deliveryId, open]);

  const handleRequestStatusChange = (status) => {
    setTargetStatus(status);
  };

  const handleConfirmStatusChange = async (comment) => {
    if (!deliveryId || !targetStatus) {
      return;
    }

    setStatusLoading(true);
    setError(null);

    try {
      const result = await updateMixDeliveryStatus(
        deliveryId,
        targetStatus,
        comment,
      );

      setDelivery(result);
      setTargetStatus(null);

      setNotification({
        open: true,
        message: 'Status dostawy został zaktualizowany.',
      });

      onStatusChanged?.(result);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!statusLoading) {
      onClose?.();
    }
  };

  // const items = delivery?.items ?? [];

  // const mixItems = items.filter((item) => isMixItem(item));

  // const fullPalletItems = items.filter((item) => !isMixItem(item));
  const isOperator = String(userRole ?? '').toUpperCase() === 'OPERATOR';

  const allItems = delivery?.items ?? [];

  const items = isOperator
    ? allItems.filter((item) => isMixItem(item))
    : allItems;

  const mixItems = items.filter((item) => isMixItem(item));

  const fullPalletItems = items.filter((item) => !isMixItem(item));
  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="xl">
        <DialogTitle>
          {delivery
            ? `Dostawa ${delivery.deliveryNumber}`
            : 'Szczegóły dostawy'}
        </DialogTitle>

        <DialogContent dividers>
          {loading && (
            <Box
              sx={{
                py: 5,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && delivery && (
            <Stack spacing={3}>
              <DeliveryHeader delivery={delivery} isOperator={isOperator} />

              <Divider />

              <Box>
                {/* <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography variant="h6">Pozycje dostawy</Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      color="warning"
                      label={`Pozycje miksowe: ${mixItems.length}`}
                    />

                    <Chip
                      size="small"
                      label={`Pozycje pełnopaletowe: ${fullPalletItems.length}`}
                    />

                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Łącznie: ${items.length}`}
                    />
                  </Stack>
                </Stack> */}
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {isOperator ? 'Pozycje miksowe' : 'Pozycje dostawy'}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 1.5 }}
                >
                  <Chip
                    size="small"
                    color="warning"
                    label={`Pozycje miksowe: ${mixItems.length}`}
                  />

                  {!isOperator && (
                    <>
                      <Chip
                        size="small"
                        label={`Pozycje pełnopaletowe: ${fullPalletItems.length}`}
                      />

                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Łącznie: ${items.length}`}
                      />
                    </>
                  )}
                </Stack>
                <DeliveryItemsTable items={items} />
              </Box>

              {delivery.events?.length > 0 && (
                <>
                  <Divider />

                  <Box>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                      Historia
                    </Typography>

                    <Stack spacing={1}>
                      {delivery.events.map((event) => (
                        <Paper
                          key={event.id}
                          variant="outlined"
                          sx={{ p: 1.5 }}
                        >
                          <Typography variant="body2">
                            {getEventLabel(event)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(event.createdAt)}
                          </Typography>

                          {event.comment && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {event.comment}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          {delivery && (
            <StatusActions
              currentStatus={delivery.status}
              loading={statusLoading}
              onStatusChange={handleRequestStatusChange}
              isOperator={isOperator}
            />
          )}

          <Button onClick={handleDialogClose} disabled={statusLoading}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>
      <MixStatusChangeDialog
        open={Boolean(targetStatus)}
        currentStatus={delivery?.status}
        targetStatus={targetStatus}
        loading={statusLoading}
        onClose={() => setTargetStatus(null)}
        onConfirm={handleConfirmStatusChange}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() =>
          setNotification({
            open: false,
            message: '',
          })
        }
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() =>
            setNotification({
              open: false,
              message: '',
            })
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function DeliveryHeader({ delivery, isOperator }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        gap: 2,
      }}
    >
      <InfoBox label="Numer dostawy" value={delivery.deliveryNumber} />

      <InfoBox label="Data dostawy" value={formatDate(delivery.deliveryDate)} />

      <InfoBox
        label="Odbiorca"
        value={delivery.recipientName || delivery.recipientNumber || '—'}
      />

      <InfoBox label="Liczba pozycji miksowych" value={delivery.mixItemCount} />

      {!isOperator && (
        <InfoBox
          label="Wszystkie pozycje"
          value={delivery.items?.length ?? 0}
        />
      )}

      <InfoBox
        label="Status pracy"
        value={<DeliveryStatusChip status={delivery.status} />}
      />

      <InfoBox
        label="Status zapasu"
        value={<StockStatusChip status={delivery.stockStatus} />}
      />

      <InfoBox label="Rozpoczęto" value={formatDateTime(delivery.startedAt)} />

      <InfoBox
        label="Przygotowano"
        value={formatDateTime(delivery.preparedAt)}
      />
      <InfoBox
        label="Kanapkowanie"
        value={
          <Chip
            size="small"
            label={delivery.sandwichingRequired ? 'TAK' : 'NIE'}
            color={delivery.sandwichingRequired ? 'warning' : 'default'}
            variant={delivery.sandwichingRequired ? 'filled' : 'outlined'}
          />
        }
      />
    </Box>
  );
}

function DeliveryItemsTable({ items }) {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth: 1500,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Poz.</TableCell>

            <TableCell>Materiał</TableCell>

            <TableCell>Nazwa</TableCell>

            <TableCell>Rodzaj</TableCell>

            <TableCell align="right">Ilość dostawy</TableCell>

            <TableCell align="right">Ilość pełna</TableCell>

            <TableCell align="right">Ilość miksowa</TableCell>

            <TableCell>JM</TableCell>

            <TableCell>Typ KEG</TableCell>

            <TableCell>Skład</TableCell>

            <TableCell align="right">0D20 przed analizą</TableCell>

            <TableCell align="right">Do uzupełnienia</TableCell>

            <TableCell align="right">Brak</TableCell>

            <TableCell>Status zapasu</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((item) => {
            const mixPosition = isMixItem(item);

            const originalStorageLocation = item.originalStorageLocation ?? '—';

            const recommendedStorageLocation =
              item.recommendedStorageLocation ?? originalStorageLocation;

            return (
              <TableRow key={item.id} hover>
                <TableCell>{item.positionNumber}</TableCell>

                <TableCell>{item.materialNumberSnapshot}</TableCell>

                <TableCell>{item.productNameSnapshot}</TableCell>

                <TableCell>
                  <Chip
                    size="small"
                    color={mixPosition ? 'warning' : 'default'}
                    label={mixPosition ? 'Miks' : 'Pełne palety'}
                  />
                </TableCell>

                <TableCell align="right">
                  {formatQuantity(item.deliveryQuantity)}
                </TableCell>

                <TableCell align="right">
                  {formatQuantity(item.fullQuantity)}
                </TableCell>

                <TableCell align="right">
                  {mixPosition ? (
                    <Typography component="span" fontWeight={700}>
                      {formatQuantity(item.mixQuantity)}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>

                <TableCell>{item.unit}</TableCell>

                <TableCell>{item.kegType ?? '—'}</TableCell>

                <TableCell>
                  {originalStorageLocation === recommendedStorageLocation
                    ? originalStorageLocation
                    : `${originalStorageLocation} → ${recommendedStorageLocation}`}
                </TableCell>

                <TableCell align="right">
                  {mixPosition ? formatQuantity(item.pickingStockBefore) : '—'}
                </TableCell>

                <TableCell align="right">
                  {mixPosition
                    ? formatQuantity(item.replenishmentQuantity)
                    : '—'}
                </TableCell>

                <TableCell align="right">
                  {Number(item.shortageQuantity ?? 0) > 0 ? (
                    <Typography
                      component="span"
                      color="error.main"
                      fontWeight={700}
                    >
                      {formatQuantity(item.shortageQuantity)}
                    </Typography>
                  ) : (
                    '—'
                  )}
                </TableCell>

                <TableCell>
                  <StockStatusChip status={item.stockStatus} />
                </TableCell>
              </TableRow>
            );
          })}

          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                Brak pozycji dostawy.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function StatusActions({ currentStatus, loading, onStatusChange, isOperator }) {
  if (loading) {
    return <CircularProgress size={22} />;
  }

  if (currentStatus === MIX_DELIVERY_STATUS.TO_PREPARE) {
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.IN_PROGRESS)}
        >
          Rozpocznij przygotowanie
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.PREPARED)}
        >
          Oznacz jako przygotowany
        </Button>
      </>
    );
  }

  if (currentStatus === MIX_DELIVERY_STATUS.IN_PROGRESS) {
    return (
      <>
        <Button
          variant="outlined"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.TO_PREPARE)}
        >
          Cofnij do przygotowania
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={() => onStatusChange(MIX_DELIVERY_STATUS.PREPARED)}
        >
          Oznacz jako przygotowany
        </Button>
      </>
    );
  }
  if (currentStatus === MIX_DELIVERY_STATUS.PREPARED && isOperator) {
    return null;
  }
  return (
    <Button
      variant="outlined"
      color="warning"
      onClick={() => onStatusChange(MIX_DELIVERY_STATUS.TO_PREPARE)}
    >
      Otwórz ponownie
    </Button>
  );
}

function InfoBox({ label, value }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Box sx={{ mt: 0.5 }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography fontWeight={600}>{value}</Typography>
        ) : (
          value
        )}
      </Box>
    </Paper>
  );
}

export function DeliveryStatusChip({ status }) {
  const colorMap = {
    [MIX_DELIVERY_STATUS.TO_PREPARE]: 'default',
    [MIX_DELIVERY_STATUS.IN_PROGRESS]: 'info',
    [MIX_DELIVERY_STATUS.PREPARED]: 'success',
  };

  return (
    <Chip
      size="small"
      color={colorMap[status] ?? 'default'}
      label={MIX_DELIVERY_STATUS_LABELS[status] ?? status}
    />
  );
}

export function StockStatusChip({ status }) {
  const colorMap = {
    [MIX_STOCK_STATUS.AVAILABLE]: 'success',
    [MIX_STOCK_STATUS.AVAILABLE_IN_PICKING_AREA]: 'success',
    [MIX_STOCK_STATUS.REPLENISHMENT_REQUIRED]: 'warning',
    [MIX_STOCK_STATUS.STORAGE_LOCATION_CHANGE_REQUIRED]: 'warning',
    [MIX_STOCK_STATUS.STOCK_SHORTAGE]: 'error',
  };

  return (
    <Chip
      size="small"
      variant="outlined"
      color={colorMap[status] ?? 'default'}
      label={MIX_STOCK_STATUS_LABELS[status] ?? status}
    />
  );
}

function getEventLabel(event) {
  if (event.eventType === 'STATUS_CHANGED') {
    return `${
      MIX_DELIVERY_STATUS_LABELS[event.previousStatus] ?? event.previousStatus
    } → ${MIX_DELIVERY_STATUS_LABELS[event.newStatus] ?? event.newStatus}`;
  }

  const labels = {
    IMPORTED: 'Dostawa została zaimportowana',
    DATA_CHANGED: 'Dane dostawy zostały zmienione',
    DEACTIVATED: 'Dostawa została dezaktywowana',
    REACTIVATED: 'Dostawa została ponownie aktywowana',
  };

  return labels[event.eventType] ?? event.eventType;
}
function isMixItem(item) {
  if (typeof item?.isMixPosition === 'boolean') {
    return item.isMixPosition;
  }

  return Number(item?.mixQuantity ?? 0) > 0;
}

function formatQuantity(value) {
  const numberValue = Number(value ?? 0);

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(numberValue);
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL').format(new Date(value));
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}
